import { RefObject, useEffect, useRef, useState } from 'react';

import URLs from '@/enums/urls';
import { useMutateUserMetadata, useUserMetadata } from '@/hooks/api/useUserMetadata';
import useGatedBitkitTour from '@/hooks/useGatedBitkitTour';
import { McpConnectionStatus } from '@/models/User';
import { queryClient } from '@/utils/query';

import McpTourVisual from './McpTourVisual';
import { McpTourDismissMethod, trackMcpTourDismissed, trackMcpTourDocsOpened, trackMcpTourShown } from './mcpTracking';

export const MCP_NAV_TOUR_KEY = 'mcp_nav_tour';

type Params = {
  enabled: boolean;
  isSideNavCollapsed: boolean;
  status: McpConnectionStatus;
  suppressed: boolean;
  targetRef: RefObject<HTMLElement | null>;
};

const useMcpNavTour = ({ enabled, isSideNavCollapsed, status, suppressed, targetRef }: Params) => {
  const { data: storedState, isSuccess } = useUserMetadata<'seen'>(MCP_NAV_TOUR_KEY, { enabled });
  const { mutate: mutateUserMetadata } = useMutateUserMetadata({ survivesNavigation: true });

  const [isSeen, setIsSeen] = useState(false);
  const hasRecordedSeen = useRef(false);
  const dismissMethod = useRef<McpTourDismissMethod>('close_button');
  const hasStartedSetupFromTour = useRef(false);

  const wantsToOpen = enabled && !suppressed && isSuccess && storedState !== 'seen' && !isSeen;
  const trackingContext = { mcpConnectionStatus: status, isSideNavCollapsed };

  const recordSeen = () => {
    if (hasRecordedSeen.current) {
      return;
    }

    hasRecordedSeen.current = true;
    setIsSeen(true);
    queryClient.setQueryData(['usermetadata', MCP_NAV_TOUR_KEY], { data: { value: 'seen' } });
    mutateUserMetadata({ [MCP_NAV_TOUR_KEY]: 'seen' });
  };

  const tour = useGatedBitkitTour({
    scope: 'global',
    onStatusChange: ({ status: tourStatus }) => {
      if (tourStatus === 'started') {
        trackMcpTourShown(trackingContext);
        return;
      }

      if (tourStatus !== 'dismissed' || !wantsToOpen) {
        return;
      }

      recordSeen();
      window.setTimeout(() => {
        if (!hasStartedSetupFromTour.current) {
          trackMcpTourDismissed({ ...trackingContext, dismissMethod: dismissMethod.current });
        }
      });
    },
    open: wantsToOpen,
    steps: [
      {
        actions: [
          {
            action: ({ dismiss }) => {
              dismissMethod.current = 'acknowledge_button';
              dismiss();
            },
            label: 'Got it',
          },
        ],
        content: <McpTourVisual />,
        description: 'Debug builds, trigger releases, and check status right from your AI tool.',
        id: 'mcp-nav',
        placement: 'right-end',
        secondaryAction: { href: URLs.BITRISE_MCP, isExternal: true, label: 'Learn more' },
        target: () => targetRef.current?.querySelector<HTMLElement>('[data-part="link-icon"]') ?? null,
        title: 'Connect the Bitrise MCP',
      },
    ],
  });

  const isVisible = tour.open;

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissMethod.current = 'other';
      }
    };

    const onClick = (event: MouseEvent) => {
      if (
        (event.target as Element | null)?.closest(
          `[data-scope="tour"][data-part="content"] a[href="${URLs.BITRISE_MCP}"]`,
        )
      ) {
        trackMcpTourDocsOpened(URLs.BITRISE_MCP);
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [isVisible]);

  const markSetupStarted = () => {
    if (isVisible) {
      hasStartedSetupFromTour.current = true;
      recordSeen();
    }
  };

  return { markSetupStarted, tour };
};

export default useMcpNavTour;
